import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Settings, BarChart3, CheckCircle, XCircle, AlertTriangle, Save, RefreshCw, Globe, Phone, Mail, MapPin, Facebook, Instagram, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminParametres() {
  const { authHeaders } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verification, setVerification] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState("tracking");
  const [message, setMessage] = useState(null);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings`, { headers: authHeaders });
      setSettings(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/analytics/summary`, { headers: authHeaders });
      setAnalytics(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchSettings();
    fetchAnalytics();
  }, [fetchSettings, fetchAnalytics]);

  const saveSettings = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await axios.put(`${API}/settings`, settings, { headers: authHeaders });
      setMessage({ type: "success", text: "Paramètres enregistrés !" });
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Erreur" });
    } finally {
      setSaving(false);
    }
  };

  const verifyTracking = async () => {
    setVerifying(true);
    try {
      const res = await axios.post(`${API}/settings/verify-tracking`, {}, { headers: authHeaders });
      setVerification(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setVerifying(false);
    }
  };

  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const importExcelDevis = async () => {
    if (!window.confirm("Importer les 89 devis historiques Excel ? Cette action est sûre et idempotente (les doublons seront ignorés).")) return;
    setImporting(true);
    setImportResult(null);
    try {
      const res = await axios.post(`${API}/admin/import-excel-devis`, {}, { headers: authHeaders });
      setImportResult({ type: "success", text: res.data.message });
    } catch (err) {
      setImportResult({ type: "error", text: err.response?.data?.detail || "Erreur lors de l'import" });
    } finally {
      setImporting(false);
    }
  };

  const sendReminderNow = async () => {
    try {
      await axios.post(`${API}/devis/send-weekly-reminders`, {}, { headers: authHeaders });
      setMessage({ type: "success", text: "Rappels envoyés ! Vérifiez les logs si aucun mariage cette semaine." });
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Erreur lors de l'envoi" });
    }
  };

  const set = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="animate-spin text-[#D4A843]" size={32} />
      </div>
    );
  }

  const TABS = [
    { id: "tracking", label: "Tracking & Analytics", icon: BarChart3 },
    { id: "rappels", label: "Rappels Mariages", icon: Mail },
    { id: "seo", label: "SEO & Contenu", icon: Globe },
    { id: "contact", label: "Contact & Social", icon: Phone },
    { id: "stats", label: "Statistiques", icon: BarChart3 },
  ];

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="parametres-title">Paramètres du site</h1>
          <p className="text-gray-500 text-sm mt-1">Configuration, tracking et analyse</p>
        </div>
        <button onClick={saveSettings} disabled={saving} data-testid="save-settings-btn"
          className="flex items-center gap-2 px-5 py-2.5 bg-[#D4A843] text-white text-sm font-semibold hover:bg-[#c49933] disabled:opacity-60">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Enregistrer
        </button>
      </div>

      {message && (
        <div className={`mb-6 p-4 flex items-center gap-3 ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message.type === 'success' ? <CheckCircle size={18} /> : <XCircle size={18} />}
          {message.text}
        </div>
      )}

        {/* Tabs - updated to blue accent */}
      <div className="flex border-b border-gray-200 mb-6">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${
              activeTab === tab.id ? 'border-[#1B2E4B] text-[#1B2E4B]' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            <tab.icon size={16} /> {tab.label}
          </button>
        ))}
      </div>

      {/* Rappels Mariages Tab */}
      {activeTab === "rappels" && (
        <div className="space-y-6">
          <div className="bg-white shadow-sm p-6">
            <h2 className="font-bold text-gray-900 mb-1">Email de rappel du mardi</h2>
            <p className="text-sm text-gray-500 mb-5">Chaque mardi, un email de confirmation est envoyé automatiquement pour les mariages du vendredi et samedi de la semaine.</p>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider flex items-center gap-1">
                  <Mail size={12} /> Email de réception des rappels
                </label>
                <input type="email" value={settings?.email_rappel_admin || ""} onChange={e => set("email_rappel_admin", e.target.value)}
                  placeholder="Ex: Rudolph.marianne@gmail.com" data-testid="email-rappel-admin"
                  className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843] max-w-sm" />
                <p className="text-xs text-gray-400 mt-1">C'est à cette adresse que les rappels du mardi seront envoyés.</p>
              </div>
              <div className="bg-[#f9f7f3] p-4 border-l-4 border-[#D4A843]">
                <p className="text-sm font-semibold text-gray-700 mb-2">Fonctionnement :</p>
                <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
                  <li>Chaque mardi à 8h00, le système détecte les mariages du vendredi et samedi</li>
                  <li>Un email est envoyé à l'adresse ci-dessus avec un lien de confirmation</li>
                  <li>Cliquez sur le lien pour confirmer / compléter les infos logistiques</li>
                  <li>Après confirmation, les emails sont envoyés automatiquement au chauffeur et à la décoratrice</li>
                </ol>
              </div>
              <div className="pt-2 flex gap-3 flex-wrap">
                <button onClick={sendReminderNow} data-testid="send-reminder-now-btn"
                  className="flex items-center gap-2 px-5 py-2.5 bg-[#1B2E4B] text-white text-sm font-semibold hover:bg-[#162440] transition-colors rounded">
                  <Mail size={16} /> Envoyer les rappels maintenant (test)
                </button>
                <button onClick={importExcelDevis} disabled={importing} data-testid="import-excel-btn"
                  className="flex items-center gap-2 px-5 py-2.5 bg-green-700 text-white text-sm font-semibold hover:bg-green-800 disabled:opacity-60 transition-colors rounded">
                  {importing ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}
                  {importing ? "Import en cours..." : "Importer 89 devis Excel (prod)"}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">Envoie immédiatement les rappels pour les mariages de cette semaine.</p>
              {importResult && (
                <div className={`mt-3 p-3 text-sm rounded flex items-center gap-2 ${importResult.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {importResult.type === 'success' ? <CheckCircle size={16} /> : <XCircle size={16} />}
                  {importResult.text}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tracking Tab */}
      {activeTab === "tracking" && (
        <div className="space-y-6">
          {/* Verification Status */}
          <div className="bg-white shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-gray-900">Vérification des balises</h2>
              <button onClick={verifyTracking} disabled={verifying} data-testid="verify-btn"
                className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-sm font-semibold hover:bg-gray-50 disabled:opacity-60">
                {verifying ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                Vérifier
              </button>
            </div>

            {verification && (
              <div className="grid grid-cols-3 gap-4">
                {[
                  { key: "google_analytics", label: "Google Analytics (GA4)", icon: "📊" },
                  { key: "google_tag_manager", label: "Google Tag Manager", icon: "🏷️" },
                  { key: "facebook_pixel", label: "Facebook Pixel", icon: "📘" },
                ].map(item => {
                  const status = verification[item.key];
                  return (
                    <div key={item.key} className={`p-4 border ${
                      status?.status === 'ok' ? 'border-green-200 bg-green-50' :
                      status?.status === 'invalid_format' ? 'border-orange-200 bg-orange-50' :
                      'border-gray-200 bg-gray-50'
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">{item.icon}</span>
                        <span className="font-semibold text-sm">{item.label}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {status?.status === 'ok' && <CheckCircle size={16} className="text-green-600" />}
                        {status?.status === 'invalid_format' && <AlertTriangle size={16} className="text-orange-500" />}
                        {status?.status === 'missing' && <XCircle size={16} className="text-gray-400" />}
                        <span className={`text-xs font-medium ${
                          status?.status === 'ok' ? 'text-green-700' :
                          status?.status === 'invalid_format' ? 'text-orange-600' :
                          'text-gray-500'
                        }`}>
                          {status?.status === 'ok' ? 'Configuré' :
                           status?.status === 'invalid_format' ? 'Format invalide' :
                           'Non configuré'}
                        </span>
                      </div>
                      {status?.id && (
                        <p className="text-xs text-gray-500 mt-1 font-mono">{status.id}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {!verification && (
              <p className="text-gray-400 text-sm">Cliquez sur "Vérifier" pour contrôler vos balises de tracking</p>
            )}
          </div>

          {/* Google Analytics */}
          <div className="bg-white shadow-sm p-6">
            <h2 className="font-bold text-gray-900 mb-1">Google Analytics (GA4)</h2>
            <p className="text-gray-500 text-xs mb-4">Analyse du trafic et comportement des visiteurs</p>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">
                ID de mesure (G-XXXXXXXXXX)
              </label>
              <input type="text" value={settings?.google_analytics_id || ""} 
                onChange={e => set("google_analytics_id", e.target.value)}
                placeholder="G-XXXXXXXXXX"
                data-testid="ga-id-input"
                className="w-full max-w-md border border-gray-200 px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-[#D4A843]" />
              <p className="text-xs text-gray-400 mt-1">
                Trouvez-le dans Google Analytics → Admin → Flux de données → ID de mesure
              </p>
            </div>
          </div>

          {/* Google Tag Manager */}
          <div className="bg-white shadow-sm p-6">
            <h2 className="font-bold text-gray-900 mb-1">Google Tag Manager</h2>
            <p className="text-gray-500 text-xs mb-4">Gestion centralisée de tous vos tags</p>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">
                ID du conteneur (GTM-XXXXXXX)
              </label>
              <input type="text" value={settings?.google_tag_manager_id || ""} 
                onChange={e => set("google_tag_manager_id", e.target.value)}
                placeholder="GTM-XXXXXXX"
                data-testid="gtm-id-input"
                className="w-full max-w-md border border-gray-200 px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-[#D4A843]" />
            </div>
          </div>

          {/* Facebook Pixel */}
          <div className="bg-white shadow-sm p-6">
            <h2 className="font-bold text-gray-900 mb-1">Facebook Pixel</h2>
            <p className="text-gray-500 text-xs mb-4">Suivi des conversions et remarketing Facebook/Instagram</p>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">
                ID du Pixel (15 chiffres)
              </label>
              <input type="text" value={settings?.facebook_pixel_id || ""} 
                onChange={e => set("facebook_pixel_id", e.target.value)}
                placeholder="123456789012345"
                data-testid="fb-pixel-input"
                className="w-full max-w-md border border-gray-200 px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-[#D4A843]" />
              <p className="text-xs text-gray-400 mt-1">
                Trouvez-le dans Facebook Business Manager → Gestionnaire d'événements
              </p>
            </div>
          </div>
        </div>
      )}

      {/* SEO Tab */}
      {activeTab === "seo" && (
        <div className="space-y-6">
          <div className="bg-white shadow-sm p-6">
            <h2 className="font-bold text-gray-900 mb-4">SEO & Métadonnées</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Titre du site</label>
                <input type="text" value={settings?.site_title || ""} 
                  onChange={e => set("site_title", e.target.value)}
                  className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Description (meta)</label>
                <textarea value={settings?.site_description || ""} 
                  onChange={e => set("site_description", e.target.value)}
                  rows={3}
                  className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843] resize-none" />
                <p className="text-xs text-gray-400 mt-1">Idéalement 150-160 caractères</p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Mots-clés (séparés par virgule)</label>
                <input type="text" value={settings?.meta_keywords || ""} 
                  onChange={e => set("meta_keywords", e.target.value)}
                  placeholder="location voiture luxe, mariage, réunion..."
                  className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]" />
              </div>
            </div>
          </div>

          <div className="bg-white shadow-sm p-6">
            <h2 className="font-bold text-gray-900 mb-4">Contenu de la page d'accueil</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Titre principal (Hero)</label>
                <input type="text" value={settings?.hero_title || ""} 
                  onChange={e => set("hero_title", e.target.value)}
                  className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Sous-titre</label>
                <input type="text" value={settings?.hero_subtitle || ""} 
                  onChange={e => set("hero_subtitle", e.target.value)}
                  className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact Tab */}
      {activeTab === "contact" && (
        <div className="space-y-6">
          <div className="bg-white shadow-sm p-6">
            <h2 className="font-bold text-gray-900 mb-4">Informations de contact</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">
                  <Phone size={12} className="inline mr-1" /> Téléphone
                </label>
                <input type="tel" value={settings?.contact_phone || ""} 
                  onChange={e => set("contact_phone", e.target.value)}
                  className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">
                  <Mail size={12} className="inline mr-1" /> Email
                </label>
                <input type="email" value={settings?.contact_email || ""} 
                  onChange={e => set("contact_email", e.target.value)}
                  className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">
                  <MapPin size={12} className="inline mr-1" /> Adresse
                </label>
                <input type="text" value={settings?.contact_address || ""} 
                  onChange={e => set("contact_address", e.target.value)}
                  className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]" />
              </div>
            </div>
          </div>

          <div className="bg-white shadow-sm p-6">
            <h2 className="font-bold text-gray-900 mb-4">Réseaux sociaux</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">
                  <Facebook size={12} className="inline mr-1" /> Page Facebook
                </label>
                <input type="url" value={settings?.facebook_url || ""} 
                  onChange={e => set("facebook_url", e.target.value)}
                  placeholder="https://facebook.com/..."
                  className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">
                  <Instagram size={12} className="inline mr-1" /> Profil Instagram
                </label>
                <input type="url" value={settings?.instagram_url || ""} 
                  onChange={e => set("instagram_url", e.target.value)}
                  placeholder="https://instagram.com/..."
                  className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Tab */}
      {activeTab === "stats" && analytics && (
        <div className="space-y-6">
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-white shadow-sm p-5">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Total devis</p>
              <p className="text-3xl font-bold text-gray-900">{analytics.totals.devis}</p>
            </div>
            <div className="bg-green-50 shadow-sm p-5">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Réservations</p>
              <p className="text-3xl font-bold text-green-600">{analytics.totals.reservations}</p>
            </div>
            <div className="bg-white shadow-sm p-5">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Clients</p>
              <p className="text-3xl font-bold text-gray-900">{analytics.totals.clients}</p>
            </div>
            <div className="bg-amber-50 shadow-sm p-5">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Taux conversion</p>
              <p className="text-3xl font-bold text-[#D4A843]">{analytics.conversion_rate}%</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white shadow-sm p-5">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">CA Total (réservé)</p>
              <p className="text-2xl font-bold text-gray-900">{analytics.totals.revenue.toLocaleString()} €</p>
            </div>
            <div className="bg-green-50 shadow-sm p-5">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Encaissé</p>
              <p className="text-2xl font-bold text-green-600">{analytics.totals.payments_received.toLocaleString()} €</p>
            </div>
            <div className="bg-orange-50 shadow-sm p-5">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Reste à encaisser</p>
              <p className="text-2xl font-bold text-orange-500">{analytics.totals.pending_payments.toLocaleString()} €</p>
            </div>
          </div>

          <div className="bg-white shadow-sm p-6">
            <h3 className="font-bold text-gray-900 mb-4">Ce mois-ci</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4">
                <p className="text-xs text-gray-500 mb-1">Nouveaux devis</p>
                <p className="text-2xl font-bold text-blue-600">{analytics.this_month.devis}</p>
              </div>
              <div className="bg-green-50 p-4">
                <p className="text-xs text-gray-500 mb-1">Nouvelles réservations</p>
                <p className="text-2xl font-bold text-green-600">{analytics.this_month.reservations}</p>
              </div>
            </div>
          </div>

          <div className="bg-white shadow-sm p-6">
            <h3 className="font-bold text-gray-900 mb-4">Activité récente</h3>
            <div className="space-y-2">
              {analytics.recent_activity.map(item => (
                <div key={item.numero} className="flex items-center justify-between py-2 border-b border-gray-50">
                  <div>
                    <span className="font-semibold text-sm text-[#D4A843]">{item.numero}</span>
                    <span className="text-sm text-gray-600 ml-2">{item.client_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded ${
                      item.statut === 'réservé' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                    }`}>{item.statut}</span>
                    <span className="text-xs text-gray-400">{item.created_at?.slice(0, 10)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
